import { FC, useEffect, useState } from "react";
import cx from "classnames";
import * as validation from "./helpers/validation";
import { IconTypes } from "../Icon";
import Tooltip, { TooltipPosition } from "../Tooltip";
import TooltipContent from "./components/TooltipContent";
import IconButton from "../../components/IconButton/IconButton";

import styles from "./styles.module.scss";

export enum PasswordStrengthBarProgress {
  WEAK = "Weak",
  GOOD = "Good",
  GREAT = "Great!",
}

interface IProps {
  value: string;
  onProgressChange: (progress: PasswordStrengthBarProgress) => void;
}

const PasswordStrengthBar: FC<IProps> = ({ value, onProgressChange }) => {
  const [progress, setProgress] = useState<PasswordStrengthBarProgress>(
    PasswordStrengthBarProgress.WEAK
  );
  const [progressBarValue, setProgressBarValue] = useState(33);

  const [containsOneLowercaseLetter, setContainsOneLowercaseLetter] =
    useState(false);
  const [containsOneUppercaseLetter, setContainsOneUppercaseLetter] =
    useState(false);
  const [isEightCharactersLong, setIsEightCharactersLong] = useState(false);
  const [containsOneNumber, setContainsOneNumber] = useState(false);

  useEffect(() => {
    const containsOneLowercaseLetter =
      validation.containsOneLowercaseLetter(value);
    const containsOneUppercaseLetter =
      validation.containsOneUppercaseLetter(value);
    const isEightCharactersLong = validation.isEightCharactersLong(value);
    const containsOneNumber = validation.containsOneNumber(value);

    setContainsOneLowercaseLetter(containsOneLowercaseLetter);
    setContainsOneUppercaseLetter(containsOneUppercaseLetter);
    setIsEightCharactersLong(isEightCharactersLong);
    setContainsOneNumber(containsOneNumber);
  }, [value]);

  useEffect(() => {
    setProgress(validation.getProgress(value));
  }, [
    containsOneLowercaseLetter,
    containsOneUppercaseLetter,
    isEightCharactersLong,
    containsOneNumber,
    value,
  ]);

  useEffect(() => {
    let newProgressBarValue;

    switch (progress) {
      case PasswordStrengthBarProgress.WEAK:
        newProgressBarValue = 33;
        break;

      case PasswordStrengthBarProgress.GOOD:
        newProgressBarValue = 50;
        break;

      case PasswordStrengthBarProgress.GREAT:
        newProgressBarValue = 100;
        break;
    }

    setProgressBarValue(newProgressBarValue);
    onProgressChange(progress);
  }, [progress, onProgressChange]);

  return (
    <div
      className={cx(styles.PasswordStrengthBar, {
        [styles.weak]: progress === PasswordStrengthBarProgress.WEAK,
        [styles.good]: progress === PasswordStrengthBarProgress.GOOD,
        [styles.great]: progress === PasswordStrengthBarProgress.GREAT,
      })}
    >
      <progress max={100} value={progressBarValue} />
      <footer
        style={{
          justifyContent:
            progress === PasswordStrengthBarProgress.WEAK
              ? "space-between"
              : "flex-start",
        }}
      >
        <span className={styles.title}>{progress}</span>

        {progress === PasswordStrengthBarProgress.WEAK && (
          <Tooltip
            tooltipId="passwordStrengthBar"
            content={<TooltipContent value={value} />}
            tooltipPosition={TooltipPosition.BottomRight}
          >
            <IconButton
              type={IconTypes.INFO}
              size={14}
              className={styles.info}
            />
          </Tooltip>
        )}
      </footer>
    </div>
  );
};

export default PasswordStrengthBar;
